import classNames from 'classnames'
import { useState } from 'react'

/**
 * Per-section loading and error states.
 *
 * The home page fetches three independent things. Whole-page spinners would
 * mean the whole screen waits on the slowest of them, and a single failed
 * query would blank sections that had already loaded fine.
 *
 * Note the failure mode this exists to avoid: `modules/funds/index.tsx` treats
 * "query errored" as "still loading" and spins forever, because it branches on
 * `!moneyPools` rather than on `isError`.
 */

export function PulseCard({ className }: { className?: string }) {
    return (
        <div
            className={classNames(
                'animate-pulse bg-white border border-divider rounded-card p-4',
                className
            )}
        >
            <div className="h-3 w-24 bg-divider rounded mb-3" />
            <div className="h-6 w-40 bg-divider rounded mb-4" />
            <div className="h-3 w-full bg-divider rounded mb-2" />
            <div className="h-3 w-2/3 bg-divider rounded" />
        </div>
    )
}

/**
 * «تلاش مجدد» tells you it is trying.
 *
 * It used to fire `onRetry` and change nothing on screen. A retry that fails
 * the same way as the first attempt therefore looks exactly like a button that
 * does nothing, and the recordings show people hammering it — it is one of the
 * rage-clicked controls on the home page, the funds list and the withdrawal
 * screens alike. The retry is also usually the *slowest* thing on the page, so
 * "no feedback" lasts for seconds.
 *
 * The pending state lives here rather than in the ten call sites so every
 * screen that already renders a SectionError gets it, and so a second tap
 * cannot start a second request while the first is still running.
 */
export function SectionError({
    onRetry,
    message = 'خطا در دریافت اطلاعات',
}: {
    onRetry?: () => void | Promise<unknown>
    message?: string
}) {
    const [retrying, setRetrying] = useState(false)
    const handleRetry = async () => {
        if (retrying || !onRetry) return
        setRetrying(true)
        try {
            await onRetry()
        } catch {
            // A retry that fails again is the ordinary case here — the caller
            // already renders the failure. Letting the rejection escape the
            // click handler would only add an unhandled rejection on top of
            // an error the user can already see.
        } finally {
            setRetrying(false)
        }
    }
    return (
        <div className="bg-white border border-divider rounded-card p-4 text-center">
            <p className="text-sm text-text-secondary">{message}</p>
            {onRetry && (
                <button
                    type="button"
                    onClick={handleRetry}
                    disabled={retrying}
                    aria-busy={retrying}
                    className="mt-2 text-sm text-green-1 font-bold disabled:opacity-60"
                >
                    {retrying ? 'در حال تلاش…' : 'تلاش مجدد'}
                </button>
            )}
        </div>
    )
}
